// Step 2, "Mapping" (UC-IMPORTS-002): the file's columns as rows — the header name, its first three values and the field it fills —
// filled from the recognised layout or saved profile, the layout list (the shipped Generic CSV and Seiko CSV and the supplier's own
// profiles, A3), the header row, separator, decimal separator and VAT basis (A1, A6, A7), Save as profile (A2) and a saved profile's
// Rename and Remove (A4). Continue stays disabled until Supplier lens code and Lab price are mapped (E1); it sends the mapping and
// starts the check. Back on a checked import shows the mapping it was checked with: Continue without a change returns to the
// Review and sends nothing, and a change first warns that the ticks will be reset (A5). The import's view names the separator and
// header row its columns were read at (`previewAt`), so a page opened afresh shows them and maps without the file (TC-IMPORTS-122);
// the columns of another separator or header row are read in the browser from the file it uploaded, and a page opened afresh asks
// for the file again before it reads them (its SHA-256 must match).
import {
  CSV_SEPARATORS, PRICE_LIST_MAPPING_FIELDS, PRICE_LIST_REQUIRED_MAPPING_FIELDS,
  type CsvSeparator, type DecimalSeparator, type ImportJobView, type ImportProfileView, type ImportWarning, type PriceListMappingField,
} from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { importJobs, importProfiles } from "../api/endpoints.imports";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { bytesOf, fileTextOf, headerKey, keepFileText, previewAt, sha256Of, sniffSeparator, textOf } from "../lib/csv";
import { DOT, formatCount, formatDate, percentFromBp } from "../lib/format";
import { useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Checkbox, ErrorAlert, Field, Modal, Panel, Select, TextInput } from "../ui/bits";
import { FIELD_LABEL, useImportAccess } from "./ImportsShared";

const SEPARATOR_LABEL: Readonly<Record<CsvSeparator, string>> = { ",": "Comma", ";": "Semicolon", "\t": "Tab" };
const SEPARATOR_VALUE: Readonly<Record<CsvSeparator, string>> = { ",": "comma", ";": "semicolon", "\t": "tab" };
const separatorOf = (v: string): CsvSeparator => (v === "semicolon" ? ";" : v === "tab" ? "\t" : ",");

interface Draft {
  columnMap: Record<string, PriceListMappingField>;
  headerRow: number;
  separator: CsvSeparator | null;
  decimalSeparator: DecimalSeparator;
  pricesIncludeVat: boolean;
  recommendedRetailIncludesVat: boolean;
}
interface LayoutChoice { name: string; shipped: boolean; profileId: string | null }

/** The separator and header row the server read the view's columns at: its own word (`previewAt`), else its mapping's. */
const serverReadOf = (job: ImportJobView): { separator: CsvSeparator; headerRow: number } | null =>
  job.previewAt ?? (job.mapping ? { separator: job.mapping.separator, headerRow: job.mapping.headerRow } : null);

function initialDraft(job: ImportJobView, text: string | null): Draft {
  if (job.mapping) return { ...job.mapping, columnMap: { ...job.mapping.columnMap } };
  // A file no layout recognised starts at the reading its columns were shown at — the server's, or this device's of the file.
  const read = serverReadOf(job);
  return { columnMap: {}, headerRow: read?.headerRow ?? 1, separator: read?.separator ?? (text ? sniffSeparator(text) : null), decimalSeparator: ".", pricesIncludeVat: false, recommendedRetailIncludesVat: true };
}

/** Two mappings that read a file alike (UC-IMPORTS-002 A5): the same columns by name, ignoring case, and the same reading. */
function sameDraft(a: Draft, m: ImportJobView["mapping"]): boolean {
  if (!m) return false;
  const cols = (map: Record<string, string>) => JSON.stringify(Object.entries(map).map(([c, f]) => [headerKey(c), f]).sort());
  return cols(a.columnMap) === cols(m.columnMap) && a.headerRow === m.headerRow && a.separator === m.separator && a.decimalSeparator === m.decimalSeparator
    && a.pricesIncludeVat === m.pricesIncludeVat && a.recommendedRetailIncludesVat === m.recommendedRetailIncludesVat;
}

/** A profile's column map spelled as this file's header spells its columns; a column the file lacks keeps the profile's spelling. */
function mapOnto(map: Record<string, PriceListMappingField>, header: readonly string[] | null): Record<string, PriceListMappingField> {
  const out: Record<string, PriceListMappingField> = {};
  for (const [c, f] of Object.entries(map)) out[header?.find((h) => headerKey(h) === headerKey(c)) ?? c] = f;
  return out;
}

export function MappingStep({ job, warnings }: { job: ImportJobView; warnings: readonly ImportWarning[] }) {
  const s = useSessionView();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const access = useImportAccess();
  const block = useWriteBlock();
  const [text, setText] = useState<string | null>(() => fileTextOf(job.id));
  const [draft, setDraft] = useState<Draft>(() => initialDraft(job, fileTextOf(job.id)));
  const [headerRowText, setHeaderRowText] = useState(String(draft.headerRow));
  const [layout, setLayout] = useState<LayoutChoice | null>(job.layout);
  const [saveAs, setSaveAs] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [savedProfile, setSavedProfile] = useState<{ id: string; name: string } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [rechoose, setRechoose] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"rename" | "remove" | null>(null);
  const profiles = useQuery({ queryKey: ["import-profiles", job.supplier?.id ?? ""], queryFn: () => importProfiles.list(job.supplier!.id), enabled: !!job.supplier });
  const profileKey = useMemo(() => newIdempotencyKey(), [profileName, draft]);
  const vatRate = s?.settings.vatRateBp ?? 1500;

  const serverRead = serverReadOf(job);
  const headerRowOk = Number.isInteger(draft.headerRow) && draft.headerRow >= 1 && draft.headerRow <= 20;
  const preview = !headerRowOk ? null
    : text && draft.separator ? previewAt(text, draft.separator, draft.headerRow)
    : serverRead && draft.separator === serverRead.separator && draft.headerRow === serverRead.headerRow ? { header: job.header, sample: job.sample } : null;
  const header = preview?.header ?? null;
  const asReadByServer = serverRead !== null && draft.separator === serverRead.separator && draft.headerRow === serverRead.headerRow;
  const mapped = new Set(Object.values(draft.columnMap));
  const missingColumns = header ? Object.keys(draft.columnMap).filter((c) => !header.some((h) => headerKey(h) === headerKey(c))) : [];
  const unmappedRequired = PRICE_LIST_REQUIRED_MAPPING_FIELDS.filter((f) => !mapped.has(f));
  const changed = !sameDraft(draft, job.mapping);
  const items = profiles.data?.data.items ?? [];
  const chosenKey = layout ? (layout.profileId ?? items.find((p) => p.shipped && p.name === layout.name)?.id ?? "") : "";
  const chosen = items.find((p) => p.id === chosenKey) ?? null;

  const edit = (next: Partial<Draft>) => { setDraft((d) => ({ ...d, ...next })); setLayout(null); setError(null); };
  const mapColumn = (column: string, field: PriceListMappingField | "") => {
    setDraft((d) => {
      const next: Record<string, PriceListMappingField> = {};
      for (const [c, f] of Object.entries(d.columnMap)) if (c !== column && f !== field) next[c] = f;
      if (field) next[column] = field;
      return { ...d, columnMap: next };
    });
    setLayout(null);
    setError(null);
  };
  const chooseLayout = (id: string) => {
    setError(null);
    if (!id) { setLayout(null); return; }
    const p = items.find((x) => x.id === id);
    if (!p) return;
    const head = text ? previewAt(text, p.separator, p.headerRow)?.header ?? null
      : serverRead && p.separator === serverRead.separator && p.headerRow === serverRead.headerRow ? job.header : null;
    setDraft({ columnMap: mapOnto(p.columnMap, head), headerRow: p.headerRow, separator: p.separator, decimalSeparator: p.decimalSeparator, pricesIncludeVat: p.pricesIncludeVat, recommendedRetailIncludesVat: p.recommendedRetailIncludesVat });
    setHeaderRowText(String(p.headerRow));
    setLayout({ name: p.name, shipped: p.shipped, profileId: p.shipped ? null : p.id });
  };

  /** A page opened afresh holds no file: the same file chosen again is read here, never sent again. */
  const chooseAgain = async (f: File | null) => {
    setRechoose(null);
    if (!f) return;
    const bytes = await bytesOf(f);
    const sha = await sha256Of(bytes);
    if (sha !== null ? sha !== job.file.sha256 : f.size !== job.file.sizeBytes) { setRechoose(`That is not the file this import was uploaded from — choose ${job.file.name}.`); return; }
    const t = textOf(bytes);
    keepFileText(job.id, t, sha);
    setText(t);
    setDraft((d) => (d.separator ? d : { ...d, separator: sniffSeparator(t) }));
  };

  const saveProfile = async (): Promise<{ id: string; name: string } | null> => {
    if (savedProfile) return savedProfile;
    const name = profileName.trim();
    if (!name) { setProfileError("Name the profile."); return null; }
    if (!job.supplier || !draft.separator) return null;
    try {
      const r = await importProfiles.create({
        supplierId: job.supplier.id, kind: "PriceList", name, separator: draft.separator, decimalSeparator: draft.decimalSeparator, headerRow: draft.headerRow,
        pricesIncludeVat: draft.pricesIncludeVat, recommendedRetailIncludesVat: draft.recommendedRetailIncludesVat, columnMap: draft.columnMap,
      }, profileKey);
      const saved = { id: r.data.id, name: r.data.name };
      setSavedProfile(saved);
      void qc.invalidateQueries({ queryKey: ["import-profiles", job.supplier.id] });
      return saved;
    } catch (err) {
      // Saved again under its key after a lost answer and answered as carried out, its answer not kept (BR-GOVERNANCE-016): the
      // profile exists. The supplier's profiles are read again and the one of this name goes on as the answer would have
      // (UC-IMPORTS-002 A2 step 2).
      if (carriedOut(err)) {
        void qc.invalidateQueries({ queryKey: ["import-profiles", job.supplier.id] });
        const found = await importProfiles.list(job.supplier.id).then((l) => l.data.items.find((p) => !p.shipped && p.name === name) ?? null, () => null);
        if (found) { const saved = { id: found.id, name: found.name }; setSavedProfile(saved); return saved; }
        setProfileError(`The profile “${name}” was saved: its answer did not arrive. Choose it under Layout.`);
        return null;
      }
      const f = isApiError(err) ? err.field("name") : undefined;
      setProfileError(f?.code === "not_unique" ? `${job.supplier.name} already has a profile named “${name}”.` : f?.code === "too_long" ? "At most 100 characters." : f?.code === "required" ? "Name the profile." : describeError(err));
      return null;
    }
  };

  const proceed = async (confirmed: boolean) => {
    if (busy || !draft.separator) return;
    setError(null);
    setProfileError(null);
    if (job.status === "Checked" && !changed) {
      setBusy(true);
      try { if (saveAs && !(await saveProfile())) return; navigate(`/price-import/${job.id}`, { replace: true }); }
      finally { setBusy(false); }
      return;
    }
    if (job.status === "Checked" && !confirmed) { setConfirming(true); return; }
    setConfirming(false);
    setBusy(true);
    try {
      const saved = saveAs ? await saveProfile() : null;
      if (saveAs && !saved) return;
      const profileId = saved?.id ?? layout?.profileId ?? (layout?.shipped ? chosen?.id ?? null : null);
      await importJobs.putMapping(job.id, {
        columnMap: draft.columnMap, headerRow: draft.headerRow, separator: draft.separator, decimalSeparator: draft.decimalSeparator,
        pricesIncludeVat: draft.pricesIncludeVat, recommendedRetailIncludesVat: draft.recommendedRetailIncludesVat, layout: saved?.name ?? layout?.name ?? null, profileId,
      });
      const checking = await importJobs.check(job.id);
      qc.setQueryData(["import", job.id], checking);
      void qc.invalidateQueries({ queryKey: ["imports"] });
      navigate(`/price-import/${job.id}`, { replace: true });
    } catch (err) {
      setError(err);
      void qc.invalidateQueries({ queryKey: ["import", job.id] });
    } finally { setBusy(false); }
  };

  const api = isApiError(error) ? error : null;
  const headerRowError = !headerRowOk ? "A line from 1 to 20." : api?.field("headerRow")?.code === "out_of_range" ? `The file has no line ${draft.headerRow} to read the column names from.` : undefined;
  const blocked = unmappedRequired.length > 0 ? "Map Supplier lens code and Lab price first" : missingColumns.length > 0 ? "Map every field to a column of this file first" : !preview ? (text ? "Choose a header row the file has" : `Choose ${job.file.name} again first`) : null;
  const sample = (i: number) => (preview?.sample ?? []).map((r) => r[i] ?? "").filter((v) => v !== "");
  const recognised = job.layout?.name ?? null;
  return (
    <>
      {warnings.filter((w) => w.code === "same_file").map((w) => (
        <Alert key={w.importId} tone="info" actions={<Link className="btn sm" to={`/price-import/${w.importId}`}>Open that import</Link>}>
          <b>This file was imported on {formatDate(w.uploadedAt)}{w.uploadedBy ? ` by ${w.uploadedBy.displayName}` : ""}.</b> Checked against the list it made, every line will be Unchanged.
        </Alert>
      ))}
      {api && <Alert tone="crit" role="alert"><b>Not mapped.</b> {api.code === "ILLEGAL_TRANSITION" ? `The import is ${String(api.meta.from ?? "no longer open to mapping")}; it was read again.` : describeError(error)}</Alert>}
      {error !== null && !api && <ErrorAlert error={error} />}
      <div className="split wide">
        <Panel title="Columns" meta={header ? `${header.length} column${header.length === 1 ? "" : "s"}${asReadByServer || (!serverRead && !text) ? `${DOT}${formatCount(job.lines)} price lines below the header` : ""}` : undefined} tight
          foot={<span className="help">Columns that map to nothing are ignored. Each field takes one column; choosing a field for a second column moves it.</span>}>
          {!preview ? (
            <div className="panel-body stack" style={{ gap: 10 }}>
              <Alert tone="info"><b>{text ? `The file has no line ${draft.headerRow} to read the column names from.` : "This page no longer holds the file it read the columns from."}</b> {text ? "Choose another header row." : `To read it ${serverRead ? "at another separator or header row" : "and map its columns"}, choose ${job.file.name} again — it is read on this device and not sent again.`}</Alert>
              {!text && (
                <Field label="The same file" htmlFor="pm-again" error={rechoose ?? undefined}>
                  <TextInput id="pm-again" type="file" accept=".csv,text/csv" onChange={(e) => void chooseAgain(e.target.files?.[0] ?? null)} />
                </Field>
              )}
            </div>
          ) : (
            <div className="tbl-scroll">
              <table>
                <thead><tr><th>Column</th><th>First values</th><th>Maps to</th></tr></thead>
                <tbody>
                  {preview.header.map((h, i) => {
                    const field = Object.entries(draft.columnMap).find(([c]) => headerKey(c) === headerKey(h))?.[1] ?? "";
                    return (
                      <tr key={`${h}-${i}`}>
                        <td className="strong">{h}</td>
                        <td className="mono small">{sample(i).join(" · ") || <span className="faint">—</span>}</td>
                        <td>
                          <Select aria-label={`Maps ${h} to`} boxClass="w-field" value={field} onChange={(e) => mapColumn(Object.keys(draft.columnMap).find((c) => headerKey(c) === headerKey(h)) ?? h, e.target.value as PriceListMappingField | "")}>
                            <option value="">— not imported</option>
                            {PRICE_LIST_MAPPING_FIELDS.map((f) => <option key={f} value={f}>{FIELD_LABEL[f]}{f === "labPrice" ? (draft.pricesIncludeVat ? " (incl VAT)" : " (ex VAT)") : ""}</option>)}
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {missingColumns.length > 0 && (
            <div className="panel-body"><Alert tone="warn"><b>Not in this file:</b> {missingColumns.map((c) => `${c} → ${FIELD_LABEL[draft.columnMap[c]!]}`).join("; ")}. Map {missingColumns.length === 1 ? "that field" : "those fields"} to a column of this file.</Alert></div>
          )}
        </Panel>
        <Panel title="How the file is read" meta={recognised ? `format recognised: ${recognised}` : "no layout recognised"}>
          <div className="stack" style={{ gap: 12 }}>
            <Field label="Layout" htmlFor="pm-layout" help={layout ? (layout.shipped ? "Shipped with Vision X — read-only" : `${job.supplier?.name ?? "The supplier"}'s own profile`) : "Mapped by hand"}>
              <div className="row c gap-8">
                <Select id="pm-layout" boxClass="grow" value={chosenKey} onChange={(e) => chooseLayout(e.target.value)}>
                  <option value="">By hand</option>
                  {items.map((p) => <option key={p.id} value={p.id}>{p.name}{p.shipped ? " (shipped)" : ""}</option>)}
                </Select>
                {access.profiles && chosen && !chosen.shipped && <>
                  <button className="btn sm" type="button" disabled={block !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => setDialog("rename")}>Rename…</button>
                  <button className="btn sm" type="button" disabled={block !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => setDialog("remove")}>Remove…</button>
                </>}
              </div>
            </Field>
            <div className="reading">
              <Field label="Header row" htmlFor="pm-header" error={headerRowError} help="The line with the column names">
                <TextInput id="pm-header" mono inputMode="numeric" boxClass="w-80" value={headerRowText} onChange={(e) => { setHeaderRowText(e.target.value); edit({ headerRow: /^\d{1,2}$/.test(e.target.value.trim()) ? Number(e.target.value.trim()) : 0 }); }} />
              </Field>
              <Field label="Separator" htmlFor="pm-sep">
                <Select id="pm-sep" value={draft.separator ? SEPARATOR_VALUE[draft.separator] : ""} onChange={(e) => edit({ separator: separatorOf(e.target.value) })}>
                  {!draft.separator && <option value="">Not known</option>}
                  {CSV_SEPARATORS.map((c) => <option key={SEPARATOR_VALUE[c]} value={SEPARATOR_VALUE[c]}>{SEPARATOR_LABEL[c]}</option>)}
                </Select>
              </Field>
              <Field label="Decimals" htmlFor="pm-dec">
                <Select id="pm-dec" value={draft.decimalSeparator === "," ? "comma" : "point"} onChange={(e) => edit({ decimalSeparator: e.target.value === "comma" ? "," : "." })}>
                  <option value="point">Decimal point</option>
                  <option value="comma">Decimal comma</option>
                </Select>
              </Field>
            </div>
            <div className="stack" style={{ gap: 4 }}>
              <Checkbox checked={draft.pricesIncludeVat} onChange={(v) => edit({ pricesIncludeVat: v })}>Prices include VAT</Checkbox>
              <span className="help">{draft.pricesIncludeVat ? `Each lab price is converted to ex VAT at ${percentFromBp(vatRate)}: R 2 173.50 is read as R 1 890.00.` : "Lab prices are read as ex VAT."}</span>
              {mapped.has("recommendedRetail") && <Checkbox checked={draft.recommendedRetailIncludesVat} onChange={(v) => edit({ recommendedRetailIncludesVat: v })}>Recommended retail prices include VAT</Checkbox>}
            </div>
            {access.profiles && job.supplier && (
              <div className="stack" style={{ gap: 6 }}>
                <Checkbox checked={saveAs} onChange={(v) => { setSaveAs(v); setProfileError(null); }}>{layout?.shipped ? "Save a copy as profile" : "Save as profile"}</Checkbox>
                {saveAs && (
                  <Field label="Profile name" required htmlFor="pm-profile" error={profileError ?? undefined} help={`For ${job.supplier.name}: its next file with these columns is recognised by it`}>
                    <TextInput id="pm-profile" value={profileName} disabled={savedProfile !== null} onChange={(e) => { setProfileName(e.target.value); setProfileError(null); }} />
                  </Field>
                )}
                {savedProfile && <span className="small">Saved as “{savedProfile.name}”.</span>}
              </div>
            )}
          </div>
        </Panel>
      </div>
      <div className="panel">
        <div className="panel-foot">
          {job.status === "Checked" || job.status === "Failed"
            ? <Link className="btn" to={`/price-import/${job.id}`} replace>Back to {job.status === "Checked" ? "Review" : "the check"}</Link>
            : <Link className="btn" to="/price-import">Price-list imports</Link>}
          <span className="help">{job.status === "Checked" ? (changed ? "Continue checks the file again with this mapping." : "Continue without a change keeps your ticks.") : "Continue checks the file against the supplier's current list — nothing changes yet."}</span>
          <span className="right" />
          {blocked && <span className="small faint">{blocked}</span>}
          <button className="btn primary" type="button" disabled={busy || blocked !== null || block !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => void proceed(false)}>{busy ? "Checking…" : "Continue"}</button>
        </div>
      </div>
      {confirming && (
        <Modal title="Check the file again?" onClose={() => setConfirming(false)} onSubmit={() => void proceed(true)} footer={<>
          <button className="btn" type="button" onClick={() => setConfirming(false)}>Keep my ticks</button>
          <button className="btn primary" type="submit" disabled={busy}>Check again</button>
        </>}>
          <p>Changing the mapping checks the file again; your ticks will be reset.</p>
        </Modal>
      )}
      {dialog === "rename" && chosen && <RenameProfile profile={chosen} onClose={() => setDialog(null)} onDone={(name) => { setDialog(null); setLayout((l) => (l ? { ...l, name } : l)); }} />}
      {dialog === "remove" && chosen && <RemoveProfile profile={chosen} onClose={() => setDialog(null)} onDone={() => { setDialog(null); setLayout(null); }} />}
    </>
  );
}

/** UC-IMPORTS-002 A4, E4: a saved profile renamed; its name stays unique among the supplier's profiles. */
function RenameProfile({ profile, onClose, onDone }: { profile: ImportProfileView; onClose: () => void; onDone: (name: string) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(profile.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (busy) return;
    if (!name.trim()) { setError("Name the profile."); return; }
    setBusy(true);
    try {
      const r = await importProfiles.patch(profile.id, { name: name.trim() });
      void qc.invalidateQueries({ queryKey: ["import-profiles"] });
      onDone(r.data.name);
    } catch (err) {
      const f = isApiError(err) ? err.field("name") : undefined;
      setError(f?.code === "not_unique" ? `${profile.supplier?.name ?? "The supplier"} already has a profile named “${name.trim()}”.` : f?.code === "too_long" ? "At most 100 characters." : describeError(err));
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Rename the profile" meta={profile.name} onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={busy}>{busy ? "Renaming…" : "Rename"}</button>
    </>}>
      <Field label="Profile name" required htmlFor="pm-rename" error={error ?? undefined}><TextInput id="pm-rename" value={name} onChange={(e) => { setName(e.target.value); setError(null); }} /></Field>
    </Modal>
  );
}

/** UC-IMPORTS-002 A4: a saved profile removed — no longer offered or recognised; imports that used it keep their own mapping. */
function RemoveProfile({ profile, onClose, onDone }: { profile: ImportProfileView; onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try { await importProfiles.remove(profile.id); void qc.invalidateQueries({ queryKey: ["import-profiles"] }); onDone(); }
    catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Remove the profile?" meta={profile.name} onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Keep</button>
      <button className="btn danger" type="submit" disabled={busy}>{busy ? "Removing…" : "Remove"}</button>
    </>}>
      {error !== null && <Alert tone="crit" role="alert"><b>Not removed.</b> {describeError(error)}</Alert>}
      <p>“{profile.name}” is no longer offered or used to recognise a file. Imports that used it keep their own mapping, and this one keeps the mapping shown.</p>
    </Modal>
  );
}
